'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt,
  faClock,
  faMapMarkerAlt,
  faBookOpen,
  faSeedling,
  faHandsHelping,
  faArrowRight,
  faChevronDown,
  faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import Table from '@/components/Table/Table';
import { Column } from '@/components/Table/Table.types';
import styles from './Sessions.module.css';


const SESSION_DATA = [
  {
    id: 1,
    month: 'September 2026',
    title: 'Newsletter',
    description: 'Application Launch & Essay Finalization roadmap. Common App and Coalition App open August 1. Senior action items: draft personal statements, request recommendation letters.',
    type: 'newsletter',
  },
  {
    id: 2,
    month: 'October 18, 2026',
    title: 'Session: Decoding Financial Gap Analysis & Preparing for a Debt Free Future',
    description: 'Learn how to navigate FAFSA, CSS Profile, and understand financial aid packages to avoid long-term debt.',
    type: 'session',
    date: 'October 18, 2026',
  },
  {
    id: 3,
    month: 'November 15, 2026',
    title: 'Drop-In',
    description: 'Drop-in office hours for one-on-one support with college applications and financial aid questions.',
    type: 'dropin',
    date: 'November 15, 2026',
  },
  {
    id: 4,
    month: 'December 2026',
    title: 'Newsletter',
    description: 'Early Decision Results & Winter Push. Students receiving ED acceptances must confirm enrollment. Finalize Regular Decision essays and forms.',
    type: 'newsletter',
  },
  {
    id: 5,
    month: 'January 17, 2027',
    title: 'Session',
    description: 'Regular Decision & ED II Deadlines workshop. Bulk RD and ED II applications due January 1–15.',
    type: 'session',
    date: 'January 17, 2027',
  },
  {
    id: 6,
    month: 'February 21, 2027',
    title: 'Drop-In',
    description: 'State Aid Deadlines & Local Scholarships drop-in. FAFSA priority deadlines and community scholarship applications.',
    type: 'dropin',
    date: 'February 21, 2027',
  },
  {
    id: 7,
    month: 'March 21, 2027',
    title: 'Session',
    description: 'Admissions Decisions & Financial Aid Audits. Compare award packages and submit appeals if needed.',
    type: 'session',
    date: 'March 21, 2027',
  },
  {
    id: 8,
    month: 'April 18, 2027',
    title: 'Drop-In',
    description: 'National Decision Day prep. Finalize enrollment deposit decisions and AP/IB exam preparation.',
    type: 'dropin',
    date: 'April 18, 2027',
  },
  {
    id: 9,
    month: 'May 2027',
    title: 'Newsletter',
    description: 'National Decision Day & AP/IB Exams wrap-up. May 1 enrollment deposit deadline and final exams.',
    type: 'newsletter',
  },
];

const TYPE_ICONS: Record<string, any> = {
  newsletter: faBookOpen,
  session: faCalendarAlt,
  dropin: faClock,
};

const TYPE_COLORS: Record<string, string> = {
  newsletter: '#4a90e2',
  session: '#f5a623',
  dropin: '#50e3c2',
};

export default function SessionsPage() {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const toggleExpand = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const columns: Column<any>[] = [
    {
      key: 'month',
      title: 'Date',
      render: (row: any) => (
        <td className={styles.cell}>
          <FontAwesomeIcon icon={faCalendarAlt} className={styles.cellIcon} />
          {row.month}
        </td>
      ),
    },
    {
      key: 'title',
      title: 'Event',
      render: (row: any) => (
        <td className={styles.cell}>
          <strong>{row.title}</strong>
        </td>
      ),
    },
    {
      key: 'type',
      title: 'Type',
      render: (row: any) => {
        const Icon = TYPE_ICONS[row.type] || faCalendarAlt;
        return (
          <td className={styles.cell}>
            <span
              className={styles.typeBadge}
              style={{ backgroundColor: TYPE_COLORS[row.type] }}
            >
              <FontAwesomeIcon icon={Icon} /> {row.type}
            </span>
          </td>
        );
      },
    },
  ];

  return (
    <div className={styles.container}>
      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.heroOverlay} />
        <div className={styles.heroContent}>
          <h1>Preparing for a Debt-Free Future</h1>
          <p>
            TimeLee Cares Foundation equips students with the tools to navigate
            high school intentionally and unlock higher education without the
            burden of long-term debt.
          </p>
          <Link href="/timeline" className={styles.ctaButton}>
            View Full Timeline <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </div>
      </section>

      {/* Session Calendar Section */}
      <section className={styles.section}>
        <div className={styles.containerNarrow}>
          <h2 className={styles.sectionTitle}>
            <FontAwesomeIcon icon={faCalendarAlt} /> Upcoming Sessions &amp; Newsletters
          </h2>
          <p className={styles.sectionDescription}>
            Save these dates! Below is your month-by-month master timeline for the
            upcoming fiscal year.
          </p>

          <div className={styles.eventList}>
            {SESSION_DATA.map((item) => {
              const isExpanded = expandedId === item.id;
              const Icon = TYPE_ICONS[item.type] || faCalendarAlt;
              const color = TYPE_COLORS[item.type];
              return (
                <div
                  key={item.id}
                  className={`${styles.eventCard} ${isExpanded ? styles.eventCardExpanded : ''}`}
                >
                  <div
                    className={styles.eventHeader}
                    onClick={() => toggleExpand(item.id)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={isExpanded}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') toggleExpand(item.id);
                    }}
                  >
                    <div className={styles.eventMeta}>
                      <span
                        className={styles.typeBadge}
                        style={{ backgroundColor: color }}
                      >
                        <FontAwesomeIcon icon={Icon} /> {item.type}
                      </span>
                      <span className={styles.eventDate}>
                        <FontAwesomeIcon icon={faCalendarAlt} /> {item.month}
                      </span>
                    </div>
                    <h3 className={styles.eventTitle}>{item.title}</h3>
                    <FontAwesomeIcon
                      icon={isExpanded ? faChevronUp : faChevronDown}
                      className={styles.expandIcon}
                    />
                  </div>
                  {isExpanded && (
                    <div className={styles.eventBody}>
                      <p>{item.description}</p>
                      {item.date && (
                        <p className={styles.eventDetail}>
                          <FontAwesomeIcon icon={faClock} />{' '}
                          {item.date}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className={styles.linksSection}>
        <div className={styles.containerNarrow}>
          <h2>Navigate Your Journey</h2>
          <div className={styles.linkGrid}>
            <Link href="/timeline" className={styles.linkCard}>
              <FontAwesomeIcon icon={faSeedling} size="2x" />
              <h3>Full Academic Timeline</h3>
              <p>Month-by-month roadmap from August 2026 to May 2027</p>
            </Link>
            <Link href="/ea-vs-ed" className={styles.linkCard}>
              <FontAwesomeIcon icon={faHandsHelping} size="2x" />
              <h3>Early Decision vs Early Action</h3>
              <p>Key differences and how to choose the right path</p>
            </Link>
            <Link href="/programs" className={styles.linkCard}>
              <FontAwesomeIcon icon={faBookOpen} size="2x" />
              <h3>All Programs</h3>
              <p>Explore our full suite of educational programs</p>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
